import React, { Fragment, useState, useRef, useEffect } from "react";
import { Row, Table } from "reactstrap";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import { FadeLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";
import { fetchTutorsRequest } from "store/actions";

const TutorDataTable = ({
  columns,
  data,
  tableClass,
  isPagination = true,
  isGlobalFilter,
  SearchPlaceholder,
  totalrows,
  loading,
  selectedSortData,
  setSelectedSortData,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  searchString,
  setSearchString,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const tableRef = useRef();
  const [filteredData, setFilteredData] = useState(data || []);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!globalFilter) {
      setFilteredData(data);
    } else {
      const lowercasedQuery = globalFilter.toLowerCase();
      setFilteredData(
        data.filter((row) =>
          Object.values(row).some(
            (value) =>
              value && value.toString().toLowerCase().includes(lowercasedQuery)
          )
        )
      );
    }
  }, [globalFilter, data]);

  const table = useReactTable({
    columns,
    data,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const serialNumberColumn = {
    header: "#",
    id: "serial",
    cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
    enableSorting: false,
    enableColumnFilter: false,
  };

  table.setOptions((prev) => ({
    ...prev,
    columns: [serialNumberColumn, ...columns],
  }));

  const { getHeaderGroups, getRowModel } = table;

  const windowSize = 3;
  const totalPages = Math.ceil(totalrows / pageSize);

  useEffect(() => {
    if (loading) return;
    const params = {
      page: pageIndex,
      limit: pageSize,
    };
    if (selectedSortData?.value && selectedSortData?.direction) {
      params.sortBy = `${selectedSortData.value}:${selectedSortData.direction}`;
    }
    if (searchString) {
      params.search = searchString;
    }
    dispatch(fetchTutorsRequest(params));
  }, [selectedSortData, pageIndex, searchString]);

  useEffect(() => {
    setPageIndex(1);
  }, [searchString]);

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  return (
    <Fragment>
      <Row className="mb-2">
        {isGlobalFilter && (
          <div className="d-flex justify-content-between gap-2 mb-2">
            <div className="d-flex justify-content-between gap-2 align-items-center">

            </div>
            <div className="d-flex justify-content-between align-items-end">
              <DebouncedInput
                value={searchString ?? ""}
                onChange={(value) => setSearchString(String(value))}
                className="form-control search-box me-2  d-inline-block"
                placeholder={SearchPlaceholder}
              />
            </div>
          </div>
        )}
      </Row>

      <div className="table-responsive" id="table-to-print" ref={tableRef}>
        <Table hover className={tableClass} bordered>
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) =>
                  !header.isPlaceholder ? (
                    <th
                      key={header.id}
                      style={{ width: "150px", verticalAlign: "middle" }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>

                        {(header.column.columnDef.accessorKey &&
                          header.column.columnDef.showFilter !== false) && (
                            <span
                              className="ms-1"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const field =
                                  header.column.columnDef.accessorKey;
                                if (!field) return;

                                if (
                                  !selectedSortData ||
                                  selectedSortData.value !== field
                                ) {
                                  setSelectedSortData({
                                    value: field,
                                    direction: "asc",
                                  });
                                } else if (selectedSortData.direction === "asc") {
                                  setSelectedSortData({
                                    value: field,
                                    direction: "desc",
                                  });
                                } else {
                                  setSelectedSortData({
                                    value: field,
                                    direction: "asc",
                                  });
                                }
                              }}
                            >
                              {selectedSortData?.value ===
                                header.column.columnDef.accessorKey ? (
                                selectedSortData.direction === "asc" ? (
                                  <i
                                    className="mdi mdi-arrow-up"
                                    style={{ fontSize: "20px" }}
                                  ></i>
                                ) : (
                                  <i
                                    className="mdi mdi-arrow-down"
                                    style={{ fontSize: "20px" }}
                                  ></i>
                                )
                              ) : (
                                <i
                                  className="mdi mdi-swap-vertical"
                                  style={{ fontSize: "20px" }}
                                ></i>
                              )}
                            </span>
                          )}
                      </div>
                    </th>
                  ) : null
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="text-center border-none"
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <FadeLoader color="#00a895" size={40} />
                  </div>
                </td>
              </tr>
            ) : data?.length <= 0 ||
              getRowModel()?.rows?.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center">
                  No Data Found
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        verticalAlign: "middle",
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        typeof cell.getValue() === "string"
                          ? cell.getValue()
                          : typeof cell.getValue() === "number"
                            ? String(cell.getValue())
                            : ""
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>

                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && totalrows > 0 && (
        <Pagination
          currentPage={pageIndex}
          totalPages={totalPages}
          totalItems={totalrows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          showInfo={true}
        />
      )}
    </Fragment>
  );
};

export default TutorDataTable;
